/**
 * mapRig — the auto-targeting pipeline (ADR-004 §2, §3): given a
 * RigDescriptor, the ontology, and a priors bundle, produce a scored mapping
 * table. A scoring pipeline over EVIDENCE, never a chain of platform
 * detectors: names score against the lexicon, geometry corroborates or
 * contradicts (flagged, not averaged — §7), assignment is global bipartite
 * matching with a hierarchy-consistency post-pass, and platform identity is
 * a REPORT, never a gate (§2.4).
 * Depends on: kernel/ir, mapping/normalize, mapping/reflection, mapping/assign,
 * retarget/convention. Depended on by: app/import, corpus tests.
 */
import type { MappingEntry, RigBone, RigDescriptor } from '../ir/rig-descriptor.js';
import type { Ontology } from '../ir/ontology.js';
import { requirePriorsCompatible, type PriorsBundle } from '../ir/priors.js';
import { SchemaValidationError } from '../ir/errors.js';
import { conventionTriad, facingFromVector } from '../retarget/convention.js';
import { vec3Cross, vec3Dot, vec3Scale } from '../math/vec3.js';
import { commonNamePrefix, normalizeName, sideToken } from './normalize.js';
import { mirrorPairsAcrossAxis, rigExtent, sagittalPlaneOffset } from './reflection.js';
import { maxWeightAssign } from './assign.js';

export interface MapRigOptions {
  /** Ontology profile whose coverage is required (e.g. 'minimal'). */
  readonly profile: string;
  /** Auto entries below this confidence are withheld to review, never promoted (§7). Default 0.55. */
  readonly minConfidence?: number;
}

export interface MappingFlag {
  readonly rigBone: string;
  readonly ontologyId: string;
  readonly reason: string;
}

export interface NeedsReviewEntry {
  readonly ontologyId: string;
  /** Best withheld candidate, if any — the review UI's one-click suggestion. */
  readonly candidate: { readonly rigBone: string; readonly confidence: number } | null;
  readonly reason: 'no-candidate' | 'below-threshold' | 'hierarchy-conflict' | 'side-contradiction';
}

export interface ConventionEvidence {
  /** Mirror pairs confirmed across the DECLARED side axis. */
  readonly declaredSidePairs: number;
  /** Mirror pairs confirmed across the declared FACING axis (the Tripo lie). */
  readonly facingAxisPairs: number;
  /** Which axis the geometry actually supports. */
  readonly observedSideAxis: 'declared-side' | 'declared-facing' | 'ambiguous';
  /**
   * The facing the EVIDENCE supports: observed side axis (mirror-pair
   * geometry) × name-vote chirality (which sign of it is "left") →
   * facing = left × up. Null when either input is indeterminate. This is
   * what promoteMeasuredFacing consumes — measured, provenance-carrying,
   * and it can disagree with the declaration in axis ('+Z' rig facing +X:
   * the goblin) or in sign alone ('+Z' rig facing -Z).
   */
  readonly measuredFacing: RigDescriptor['facing'] | null;
}

export interface MappingReport {
  /** Exact-synonym hits per knowledge source — a report, NEVER a gate (§2.4). */
  readonly platformAffinity: ReadonlyArray<{ readonly source: string; readonly hits: number }>;
  /** Rig-wide normalized name prefix detected from the data ('' when none). */
  readonly namePrefix: string;
  readonly convention: ConventionEvidence;
  /** Rig bones with no accepted mapping — enumerated, never silent. */
  readonly unmappedRigBones: readonly string[];
  /** Evidence contradictions — flagged, not averaged (§7). */
  readonly flags: readonly MappingFlag[];
  /** Chains whose name-assigned targets were reordered by structure (e.g. reversed spine numbering). */
  readonly chainRepairs: ReadonlyArray<{ readonly rigBones: readonly string[]; readonly ontologyIds: readonly string[] }>;
}

export interface MapRigResult {
  /** Accepted auto entries (confidence ≥ threshold, hierarchy-consistent). */
  readonly mapping: readonly MappingEntry[];
  /** Required-profile gaps the app MUST surface (§7) — empty iff ok. */
  readonly needsReview: readonly NeedsReviewEntry[];
  readonly ok: boolean;
  readonly report: MappingReport;
}

const STRIPPED_MATCH_SCORE = 0.95;
const SIDE_CONTRADICTION_FACTOR = 0.3;
const SIDED_BONE_ON_CENTER_FACTOR = 0.5;
const CHAIN_REPAIR_FACTOR = 0.85;
const SUBTREE_COVERAGE_FACTOR = 0.9;
const SIDE_COORD_THRESHOLD = 0.02; // fraction of rig extent

interface Candidate {
  score: number;
  readonly sources: string[];
  flagged: string | null;
  /**
   * Set when name evidence and bind geometry disagree about the side (§7).
   * Structural, not derived from `flagged`'s prose: the review reason is a
   * CONTRACT, and reconstructing it by string-matching a human-readable
   * sentence would couple the two silently — the sentence would drift and the
   * reason would follow it into `below-threshold` without a test noticing.
   */
  sideContradicted: boolean;
}

export function mapRig(
  rig: RigDescriptor,
  ontology: Ontology,
  priors: PriorsBundle,
  options: MapRigOptions,
): MapRigResult {
  requirePriorsCompatible(priors, ontology);
  const profileIds = ontology.profiles[options.profile];
  if (!profileIds) {
    throw new SchemaValidationError('MapRig', [
      `unknown ontology profile "${options.profile}" (known: ${Object.keys(ontology.profiles).join(', ')})`,
    ]);
  }
  const minConfidence = options.minConfidence ?? 0.55;

  // ---- Synonym index: normalized name → matching ids. The ontology's own
  //      ids are always synonyms of themselves (source 'ontology:id').
  const synonymIndex = new Map<string, Array<{ id: string; source: string }>>();
  const indexSynonym = (name: string, id: string, source: string): void => {
    const key = normalizeName(name);
    const list = synonymIndex.get(key) ?? [];
    list.push({ id, source });
    synonymIndex.set(key, list);
  };
  for (const id of ontology.byId.keys()) indexSynonym(id, id, 'ontology:id');
  for (const entry of priors.lexicon) {
    for (const s of entry.synonyms) indexSynonym(s.name, entry.ontologyId, s.source);
  }

  // ---- Geometry groundwork: observed side axis (the Tripo lesson) + chirality.
  const triad = conventionTriad(rig);
  if (triad === null) {
    throw new SchemaValidationError('MapRig', [`degenerate convention on rig "${rig.label}"`]);
  }
  const sidePairs = mirrorPairsAcrossAxis(rig.bones, triad.side).mirrorPairs.length;
  const facingPairs = mirrorPairsAcrossAxis(rig.bones, triad.facing).mirrorPairs.length;
  const observedSideAxis: ConventionEvidence['observedSideAxis'] =
    facingPairs > sidePairs ? 'declared-facing' : sidePairs > 0 ? 'declared-side' : 'ambiguous';
  const sideVec = observedSideAxis === 'declared-facing' ? triad.facing : triad.side;

  const extent = rigExtent(rig.bones);
  // The body's own midline, not every bone's median — a tail curled to one side must not move it.
  const midline = sagittalPlaneOffset(rig.bones, sideVec);
  const sideCoordOf = (b: RigBone): number => vec3Dot(b.worldBind.position, sideVec) - midline;

  // Chirality: which coordinate sign is "left"? Name-sided bones vote.
  let leftVotes = 0;
  let voteCount = 0;
  for (const b of rig.bones) {
    const token = sideToken(b.name);
    const coord = sideCoordOf(b);
    if (token === null || Math.abs(coord) < SIDE_COORD_THRESHOLD * extent) continue;
    leftVotes += (token.side === 'left') === coord > 0 ? 1 : -1;
    voteCount += 1;
  }
  const leftSign: 1 | -1 | null = voteCount === 0 ? null : leftVotes > 0 ? 1 : leftVotes < 0 ? -1 : null;

  // Measured facing (ADR-004 geometric evidence → promotable convention):
  // the observed sagittal-plane normal fixes the side AXIS; the chirality
  // vote fixes which sign of it is LEFT; facing follows by the triad's own
  // handedness (side = up × facing ⇔ facing = left × up). Both inputs are
  // measured/name evidence — nothing here trusts the declaration under test.
  let measuredFacing: RigDescriptor['facing'] | null = null;
  if (observedSideAxis !== 'ambiguous' && leftSign !== null) {
    measuredFacing = facingFromVector(vec3Cross(vec3Scale(sideVec, leftSign), triad.up));
  }

  // ---- Evidence: name scores, geometry corroborates or CONTRADICTS (§7).
  const prefix = commonNamePrefix(rig.bones.map((b) => b.name));
  const sourceHits = new Map<string, number>();
  const candidates = new Map<string, Map<string, Candidate>>(); // rigBone → id → candidate

  for (const bone of rig.bones) {
    const perId = new Map<string, Candidate>();
    const normalized = normalizeName(bone.name);
    const forms: Array<{ key: string; score: number }> = [{ key: normalized, score: 1 }];
    if (prefix.length > 0 && normalized.startsWith(prefix) && normalized.length > prefix.length) {
      forms.push({ key: normalized.slice(prefix.length), score: STRIPPED_MATCH_SCORE });
    }
    for (const form of forms) {
      for (const match of synonymIndex.get(form.key) ?? []) {
        const existing = perId.get(match.id);
        if (existing) {
          existing.score = Math.max(existing.score, form.score);
          existing.sources.push(match.source);
        } else {
          perId.set(match.id, {
            score: form.score,
            sources: [match.source],
            flagged: null,
            sideContradicted: false,
          });
        }
        // Affinity counts EXACT authored-name hits per knowledge source —
        // self-identity is not a platform, prefix-stripped hits are not exact.
        if (form.score === 1 && match.source !== 'ontology:id') {
          sourceHits.set(match.source, (sourceHits.get(match.source) ?? 0) + 1);
        }
      }
    }

    // Geometric side check against every candidate id.
    const coord = sideCoordOf(bone);
    const sided = Math.abs(coord) >= SIDE_COORD_THRESHOLD * extent;
    for (const [id, candidate] of perId) {
      const ontSide = ontology.byId.get(id)?.side ?? 'center';
      if (leftSign !== null && sided && ontSide !== 'center') {
        const geometricSide = coord * leftSign > 0 ? 'left' : 'right';
        if (geometricSide !== ontSide) {
          candidate.score *= SIDE_CONTRADICTION_FACTOR;
          candidate.flagged = `name evidence says ${ontSide}, bind geometry says ${geometricSide}`;
          // The demotion is not the finding — WHY it was demoted is. Carry the
          // cause so the review entry can name it (§7); a bare score cannot.
          candidate.sideContradicted = true;
        }
      } else if (sided && ontSide === 'center') {
        candidate.score *= SIDED_BONE_ON_CENTER_FACTOR;
        candidate.flagged = `candidate is a center bone but the bind position is clearly sided`;
      }
    }
    if (perId.size > 0) candidates.set(bone.name, perId);
  }

  // ---- Subtree-coverage corroboration (§2.2): when several rig bones tie on
  //      NAME for one ontology id, structure discriminates. Tripo stacks a
  //      co-located Hip/Pelvis pair where both are perfect Hips synonyms, but
  //      only Hip's subtree spans the legs AND the spine — mapping Pelvis
  //      leaves the torso behind under root motion (PR #19, Allen-ratified
  //      corpus sharpening). Relative evidence: an id's ontology children
  //      should find their own candidates inside the winner's subtree, and a
  //      candidate covering strictly fewer of them than its best rival is
  //      demoted — corroborate-or-demote, same shape as the side check above.
  {
    const bonesForId = new Map<string, string[]>();
    for (const [bone, perId] of candidates) {
      for (const [id, c] of perId) {
        if (c.score <= 0) continue;
        const list = bonesForId.get(id) ?? [];
        list.push(bone);
        bonesForId.set(id, list);
      }
    }
    const ontChildren = new Map<string, string[]>();
    for (const [id, b] of ontology.byId) {
      if (b.parent === null) continue;
      const list = ontChildren.get(b.parent) ?? [];
      list.push(id);
      ontChildren.set(b.parent, list);
    }
    const rigChildren = new Map<string, string[]>();
    for (const b of rig.bones) {
      if (b.parent === null) continue;
      const list = rigChildren.get(b.parent) ?? [];
      list.push(b.name);
      rigChildren.set(b.parent, list);
    }
    const descendantsOf = (bone: string): Set<string> => {
      const out = new Set<string>();
      const stack = [...(rigChildren.get(bone) ?? [])];
      for (let n = stack.pop(); n !== undefined; n = stack.pop()) {
        out.add(n);
        for (const c of rigChildren.get(n) ?? []) stack.push(c);
      }
      return out;
    };

    for (const [id, contenders] of bonesForId) {
      if (contenders.length < 2) continue;
      // Only ontology children that have a candidate SOMEWHERE count: a rig
      // that simply lacks a chain must not penalize every contender for it.
      const relevant = (ontChildren.get(id) ?? []).filter((c) => bonesForId.has(c));
      if (relevant.length === 0) continue;
      const coverage = contenders.map((bone) => {
        const desc = descendantsOf(bone);
        return relevant.filter((c) => (bonesForId.get(c) ?? []).some((rb) => desc.has(rb))).length;
      });
      const best = Math.max(...coverage);
      contenders.forEach((bone, i) => {
        if ((coverage[i] as number) >= best) return;
        const candidate = candidates.get(bone)?.get(id) as Candidate;
        candidate.score *= SUBTREE_COVERAGE_FACTOR;
        candidate.flagged ??=
          `subtree coverage: covers ${coverage[i]}/${relevant.length} of ${id}'s ontology children; ` +
          `a rival candidate covers ${best}`;
      });
    }
  }

  // ---- Global assignment (§2.2) over the positive-score candidates.
  const boneNames = rig.bones.map((b) => b.name);
  const ids = [...ontology.byId.keys()];
  const idIndex = new Map(ids.map((id, i) => [id, i]));
  const matrix = boneNames.map((name) => {
    const row = new Array<number>(ids.length).fill(0);
    for (const [id, c] of candidates.get(name) ?? []) row[idIndex.get(id) as number] = c.score;
    return row;
  });
  const assigned = maxWeightAssign(matrix);

  const flags: MappingFlag[] = [];
  /**
   * `sideContradicted` rides WITH the bone from here on. Chain repair rewrites
   * an accepted entry's ontology id, so an id-keyed set frozen at assignment
   * time strands the diagnosis on the PRE-repair id — lost from the bone's
   * eventual review entry, and a lying label if the old id ever reaches review
   * for unrelated reasons (Codex P2, #49: the reason ladder's own
   * lossy-symptom bug, one level up).
   */
  const accepted = new Map<string, { id: string; confidence: number; sideContradicted: boolean }>();
  for (let r = 0; r < boneNames.length; r += 1) {
    const col = assigned[r];
    if (col === null || col === undefined) continue;
    const bone = boneNames[r] as string;
    const id = ids[col] as string;
    const candidate = candidates.get(bone)?.get(id) as Candidate;
    if (candidate.flagged !== null) {
      flags.push({ rigBone: bone, ontologyId: id, reason: candidate.flagged });
    }
    accepted.set(bone, { id, confidence: candidate.score, sideContradicted: candidate.sideContradicted });
  }

  // ---- Hierarchy consistency (§2.2): a child may not map above its parent's
  //      target. Violating groups that form matching chains are REPAIRED by
  //      structure (reversed spine numbering is a real generated-model wart);
  //      anything else is demoted to review — flagged, never silently wrong.
  const rigParent = new Map(rig.bones.map((b) => [b.name, b.parent]));
  const ontDepth = (id: string): number => {
    let depth = 0;
    let cur = ontology.byId.get(id)?.parent ?? null;
    while (cur !== null) {
      depth += 1;
      cur = ontology.byId.get(cur)?.parent ?? null;
    }
    return depth;
  };
  const ontIsAncestor = (maybeAncestor: string, id: string): boolean => {
    let cur = ontology.byId.get(id)?.parent ?? null;
    while (cur !== null) {
      if (cur === maybeAncestor) return true;
      cur = ontology.byId.get(cur)?.parent ?? null;
    }
    return false;
  };

  const findViolations = (map: Map<string, { id: string; confidence: number }>): Set<string> => {
    const violating = new Set<string>();
    for (const [bone, { id }] of map) {
      let ancestor = rigParent.get(bone) ?? null;
      while (ancestor !== null) {
        const ancestorEntry = map.get(ancestor);
        if (ancestorEntry && ontIsAncestor(id, ancestorEntry.id)) {
          // The rig ancestor's target is a DESCENDANT of this bone's target.
          violating.add(bone);
          violating.add(ancestor);
        }
        ancestor = rigParent.get(ancestor) ?? null;
      }
    }
    return violating;
  };

  const chainRepairs: Array<{ rigBones: string[]; ontologyIds: string[] }> = [];
  let violating = findViolations(accepted);
  if (violating.size > 0) {
    // Group violating bones into maximal rig chains (parent-child runs).
    const inViolation = (n: string | null | undefined): boolean => n != null && violating.has(n);
    const chains: string[][] = [];
    const seen = new Set<string>();
    for (const bone of violating) {
      if (seen.has(bone)) continue;
      // Walk to the top of this violating run.
      let top = bone;
      while (inViolation(rigParent.get(top))) top = rigParent.get(top) as string;
      const chain: string[] = [];
      let cur: string | null = top;
      while (cur !== null && violating.has(cur)) {
        chain.push(cur);
        seen.add(cur);
        const children = rig.bones.filter((b) => b.parent === cur && violating.has(b.name));
        cur = children.length === 1 ? (children[0]?.name ?? null) : null;
      }
      chains.push(chain);
    }
    for (const chain of chains) {
      const targets = chain.map((b) => (accepted.get(b) as { id: string }).id);
      // Repairable iff the target SET forms one ontology chain: reassign in depth order.
      const byDepth = [...targets].sort((a, b) => ontDepth(a) - ontDepth(b));
      const isOntChain = byDepth.every(
        (id, i) => i === 0 || ontology.byId.get(id)?.parent === byDepth[i - 1],
      );
      if (isOntChain && chain.length === targets.length && chain.length > 1) {
        chain.forEach((bone, i) => {
          const prior = accepted.get(bone) as { confidence: number; sideContradicted: boolean };
          accepted.set(bone, {
            id: byDepth[i] as string,
            confidence: prior.confidence * CHAIN_REPAIR_FACTOR,
            // The repair moves the bone to a new id; the contradiction is a
            // fact about the bone's own name-vs-geometry evidence and moves
            // with it (Codex P2, #49).
            sideContradicted: prior.sideContradicted,
          });
          flags.push({
            rigBone: bone,
            ontologyId: byDepth[i] as string,
            reason: 'chain-order repair: name evidence conflicted with hierarchy; structure won',
          });
        });
        chainRepairs.push({ rigBones: chain, ontologyIds: byDepth });
      }
    }
    // Anything still violating after repairs is demoted — never silently wrong.
    violating = findViolations(accepted);
    for (const bone of violating) {
      const entry = accepted.get(bone) as { id: string };
      flags.push({ rigBone: bone, ontologyId: entry.id, reason: 'hierarchy conflict: demoted to review' });
      accepted.delete(bone);
    }
  }

  // ---- Threshold + result assembly. Below-threshold entries are withheld
  //      to review — the kernel never silently promotes a weak guess (§7).
  const withheld = new Map<string, { rigBone: string; confidence: number; sideContradicted: boolean }>();
  const mapping: MappingEntry[] = [];
  for (const [bone, { id, confidence, sideContradicted }] of accepted) {
    if (confidence >= minConfidence) {
      mapping.push({ rigBone: bone, ontologyId: id, confidence, provenance: 'auto' });
    } else {
      const best = withheld.get(id);
      if (!best || best.confidence < confidence) withheld.set(id, { rigBone: bone, confidence, sideContradicted });
    }
  }
  mapping.sort((a, b) => a.rigBone.localeCompare(b.rigBone));

  const mappedIds = new Set(mapping.map((m) => m.ontologyId));
  const demotedIds = new Set(flags.filter((f) => f.reason.startsWith('hierarchy conflict')).map((f) => f.ontologyId));
  const needsReview: NeedsReviewEntry[] = [];
  for (const id of profileIds) {
    if (mappedIds.has(id)) continue;
    const candidate = withheld.get(id) ?? null;
    needsReview.push({
      ontologyId: id,
      candidate: candidate === null ? null : { rigBone: candidate.rigBone, confidence: candidate.confidence },
      // Most SPECIFIC cause first. A side contradiction always lands below the
      // threshold — an exact name match scores 1 (the best any candidate gets),
      // so 1 × SIDE_CONTRADICTION_FACTOR = 0.3 < the 0.55 default, and every
      // weaker form only lands lower. Testing `candidate !== null` first would
      // therefore swallow it into 'below-threshold' EVERY time — which is what
      // it did until 2026-07-15, leaving 'side-contradiction' unreachable in a
      // union that advertised it. The threshold is the SYMPTOM; the
      // contradiction is the diagnosis, and the review UI needs the diagnosis
      // to say anything useful to a human. The flag is read off the withheld
      // candidate itself — it traveled with the bone through chain repair, so
      // a repaired bone keeps its diagnosis under its NEW id and the old id
      // cannot inherit one it no longer owns (Codex P2, #49).
      reason: candidate?.sideContradicted
        ? 'side-contradiction'
        : candidate !== null
          ? 'below-threshold'
          : demotedIds.has(id)
            ? 'hierarchy-conflict'
            : 'no-candidate',
    });
  }

  const mappedBones = new Set(mapping.map((m) => m.rigBone));
  return {
    mapping,
    needsReview,
    ok: needsReview.length === 0,
    report: {
      platformAffinity: [...sourceHits.entries()]
        .map(([source, hits]) => ({ source, hits }))
        .sort((a, b) => b.hits - a.hits || a.source.localeCompare(b.source)),
      namePrefix: prefix,
      convention: { declaredSidePairs: sidePairs, facingAxisPairs: facingPairs, observedSideAxis, measuredFacing },
      unmappedRigBones: rig.bones.map((b) => b.name).filter((n) => !mappedBones.has(n)),
      flags,
      chainRepairs,
    },
  };
}
